import { useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useMemo, useState } from 'react';
import useSession from '../../hooks/useSession';
import UserTopScore from '../organisms/UserTopScore';
import NavbarTemplate from '../templates/NavbarTemplate';
import Feedback from '../atoms/Feedback';
import SelectMultiple from '../atoms/SelectMultiple';
import getCategories from '../../services/getCategories';
import Button from '../atoms/Button';
import useToast from '../../hooks/useToast';
import AlertBox from '../atoms/AlertBox';

/**
 * Recruiter page is a control panel for recruiters
 */
function RecruiterPage() {
    const { session } = useSession();
    const { createToast, toastElement } = useToast();
    
    const [selectedCategories, setSelectedCategories] = useState([]);  
    const [categoriesToSearch, setCategoriesToSearch] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
  
    // Fetch recruiter's categories
    const {
      data: categoriesData,
      isLoading: isCategoriesLoading,
      isError: isCategoriesError,
    } = useQuery({
      queryKey: [session, 'recruiterCategories'],
      queryFn: () => getCategories(session, i18next.language),
      enabled: !!session,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    });
  
    // Transform category data to structure expected by SelectMultiple
    const categoryOptions = useMemo(() => {
      if (!categoriesData?.categories) return {};
      
      return {
        categories: categoriesData.categories.reduce((acc, category) => {
          acc[category.id] = category.name;
          return acc;
        }, {}),
      };
    }, [categoriesData]);

    /**
     * Handler for updating selected categories in SelectMultiple
     */
    const handleCategorySelect = (values) => {
      setSelectedCategories(values);
    };
    
    /**
     * Handler for updating search loading state
     */
    const handleSearchLoadingChange = (loading) => {
      setIsSearchLoading(loading);
    };
    
    const handleSearch = () => {
      if (selectedCategories.length === 0) {
        createToast('error', i18next.t('select-at-least-one-category'));
        return;
      }
  
      const mappedNames = selectedCategories.map((catId) => 
        categoryOptions.categories[catId]
      );
  
      setCategoriesToSearch(mappedNames);
    };

    const isButtonDisabled = isCategoriesLoading || 
      (isSearchLoading && categoriesToSearch.length > 0);

    return (
        <NavbarTemplate>
          {toastElement}

          <div className="w-full flex flex-col gap-5">
            {isCategoriesError && (
              <Feedback type="error">
                {i18next.t('error-loading-categories')}
              </Feedback>
            )}
    
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="w-full md:w-4/5">
                <SelectMultiple
                  isLoading={isCategoriesLoading}
                  options={categoryOptions}
                  selected={selectedCategories}
                  onSelect={handleCategorySelect}
                  placeholder={i18next.t('select-categories')}
                  color="gray"
                />
              </div>

              <div className="w-full md:w-1/5 flex justify-center md:justify-end">
                <Button
                  isLoading={isCategoriesLoading || (isSearchLoading && categoriesToSearch.length > 0)}
                  disabled={isButtonDisabled}
                  color="orange"
                  onClick={handleSearch}
                  title={i18next.t('search')}
                  icon={faSearch}
                >
                  {i18next.t('search')}
                </Button>
              </div>
            </div>
            
            {categoriesToSearch.length === 0 && (
              <AlertBox color="orange" className="max-w-7xl mx-auto">
                <div className="space-y-3">
                  <h2 className="text-xl font-bold text-black">🌟 ¡Bienvenido a la página de Reclutadores! 🌟</h2>
                  <p className="text-black">
                    Aquí puedes encontrar y conectar con los mejores talentos. ¡Descubre cómo funciona! 👇
                  </p>
                  <ul className="list-disc list-inside space-y-2 pl-4">
                    <li>
                      🎯 <span className="font-semibold">Lista talentos por categorías:</span> Los talentos se ordenan según los puntajes que han obtenido en sus categorías. ¡Encuentra a los mejores!
                    </li>
                    <li>
                      ✉️ <span className="font-semibold">Invita a competencias:</span> Selecciona talentos y envíales una invitación por correo electrónico para que participen en competencias.
                    </li>
                    <li>
                      💬 <span className="font-semibold">Mensajes por WhatsApp:</span> También puedes enviarles un mensaje directo por WhatsApp para invitarlos.
                    </li>
                    <li>
                      🚀 <span className="font-semibold">Futuras mejoras:</span> Pronto podrás adjuntar archivos (docx o pdf) para escanear y encontrar talentos que coincidan con las categorías de tu competencia. ¡Innovación constante!
                    </li>
                  </ul>
                  <p className="text-black italic">
                    ¡Empieza a explorar y encuentra a los talentos que necesitas! 🚀
                  </p>
                </div>
              </AlertBox>
            )}

            {categoriesToSearch.length > 0 && (
              <UserTopScore
                categoriesList={categoriesToSearch}
                onLoadingChange={handleSearchLoadingChange}
              />
            )}
          </div>
        </NavbarTemplate>
      );
    }
    
    export default RecruiterPage;